/**
 * One page's annotation overlay (returned by renderPageOverlay): two <PageSvg> layers in page units —
 * highlights (multiply blend) and ink/text/symbols — plus the wet-stroke elements the pointer
 * handlers draw into, and in edit mode the selection frame/handles and connector markers.
 * Subscribes to its own page in the overlay store, so edits elsewhere don't re-render it.
 */
import { Crosshair, Move, MoveDiagonal2 } from 'lucide-react';
import { memo, useLayoutEffect, useRef } from 'react';
import type { AnnotationObject, AnnotationOf } from '../../shared/annotations';
import type { PageSize } from '../../shared/dto/library';
import { useT } from '../i18n';
import { PageSvg } from '../viewer/PageSvg';
import { PinMark } from '../viewer/Pins';
import { pinDiameterPx } from '../viewer/pinSize';
import { useAnnotateCtx, usePageObjects, usePageUi } from './context';
import {
  effectiveScale,
  handleOffsetPx,
  HANDLE_TARGET_PX,
  hitContext,
  objectBounds,
  pageHeightUnits,
  scaledPinBounds,
  scaledPinSize,
  SELECTION_PAD_PX,
  UNITS_W,
  type Pt,
  type Rect,
} from './geometry';
import { HighlightShape, ObjectShape } from './shapes';
import type { DragPreview } from './store';

export const HIGHLIGHT_OPACITY = 0.35;
/** Fixed colours on the (always white) page. */
export const SELECTION_COLOR = '#2a41a8';
const FALLBACK_PAGE: PageSize = { w: 1, h: Math.SQRT2 };

function previewTransform(p: DragPreview, obj: AnnotationObject, H: number): string {
  const k = effectiveScale(obj, p.k);
  const ox = p.ox * UNITS_W;
  const oy = p.oy * H;
  return `translate(${p.dx * UNITS_W} ${p.dy * H}) translate(${ox} ${oy}) scale(${k}) translate(${-ox} ${-oy})`;
}

/** Bounds `r` as the drag preview leaves them; `w` = page box width (CSS px). */
function previewRect(r: Rect, p: DragPreview | null, obj: AnnotationObject, H: number, w: number): Rect {
  if (!p) return r;
  const dx = p.dx * UNITS_W;
  const dy = p.dy * H;
  // A pin as it will land, about its centre: the same size as the ghost pin (never below the minimum).
  if (obj.kind === 'connector') return scaledPinBounds(obj, { x: (r.x0 + r.x1) / 2 + dx, y: (r.y0 + r.y1) / 2 + dy }, p.k, w);
  const k = effectiveScale(obj, p.k);
  const ox = p.ox * UNITS_W;
  const oy = p.oy * H;
  return { x0: ox + (r.x0 - ox) * k + dx, y0: oy + (r.y0 - oy) * k + dy, x1: ox + (r.x1 - ox) * k + dx, y1: oy + (r.y1 - oy) * k + dy };
}

const pct = (v: number, of: number) => `${(v / of) * 100}%`;

export const PageOverlay = memo(function PageOverlay({ page, w }: { page: number; w: number; h: number }) {
  const { store, pages } = useAnnotateCtx();
  const objects = usePageObjects(store, page);
  const ui = usePageUi(store, page);
  const size = pages[page] ?? FALLBACK_PAGE;
  const H = pageHeightUnits(size);
  const wetInk = useRef<SVGPathElement>(null);
  const wetHl = useRef<SVGLineElement>(null);

  useLayoutEffect(() => {
    store.registerWet(page, { ink: wetInk.current, highlight: wetHl.current });
    return () => store.registerWet(page, null);
  }, [store, page]);

  // A committed stroke's wet preview (or a move preview / eraser fade) ends in the same frame the
  // real change appears.
  useLayoutEffect(() => {
    const p = store.pendingWet;
    if (p && p.page === page && objects.some((o) => o.id === p.id)) store.releaseWet();
    store.checkSettled(page, objects);
  }, [objects, store, page]);

  const highlights: AnnotationOf<'highlight'>[] = [];
  const others: AnnotationObject[] = [];
  let selected: AnnotationObject | undefined;
  for (const o of objects) {
    if (o.id === ui.selectedId) selected = o;
    if (o.id === ui.hiddenId) continue;
    if (o.kind === 'highlight') highlights.push(o);
    else if (o.kind !== 'connector') others.push(o);
  }
  const preview = ui.preview && selected && ui.preview.id === selected.id ? ui.preview : null;
  const tf = (o: AnnotationObject) => (preview && preview.id === o.id ? previewTransform(preview, o, H) : undefined);
  const bounds = selected ? objectBounds(selected, hitContext(H, w)) : null;
  // A connector being moved or resized: its pin as it will land (the viewer's own pin stays put till then).
  const ghostPin =
    selected?.kind === 'connector' && preview ? { color: selected.data.color, d: pinDiameterPx(scaledPinSize(selected, preview.k, w), w) } : null;

  return (
    <>
      <PageSvg page={size} style={{ mixBlendMode: 'multiply', opacity: HIGHLIGHT_OPACITY, pointerEvents: 'none' }}>
        {highlights.map((o) => (
          <HighlightShape key={o.id} obj={o} H={H} faded={ui.erasing.has(o.id)} transform={tf(o)} />
        ))}
        <line ref={wetHl} visibility="hidden" strokeLinecap="butt" />
      </PageSvg>
      <PageSvg page={size} style={{ pointerEvents: 'none' }}>
        {others.map((o) => (
          <ObjectShape key={o.id} obj={o} H={H} faded={ui.erasing.has(o.id)} transform={tf(o)} />
        ))}
        <path ref={wetInk} />
        {ui.editing && selected && bounds && selected.id !== ui.hiddenId ? (
          <SelectionFrame rect={previewRect(bounds, preview, selected, H, w)} pad={(SELECTION_PAD_PX * UNITS_W) / Math.max(1, w)} />
        ) : null}
      </PageSvg>
      {ui.editing && selected && bounds && selected.id !== ui.hiddenId ? (
        <SelectionHandles
          rect={previewRect(bounds, preview, selected, H, w)}
          // From the committed size, so the handles don't jump while a resize drag crosses 44 px.
          offset={handleOffsetPx(((bounds.x1 - bounds.x0) * w) / UNITS_W, ((bounds.y1 - bounds.y0) * w) / UNITS_W)}
          H={H}
          scalable={ui.scalable}
          ghostPin={ghostPin}
          grab={ui.grab}
        />
      ) : null}
      {ui.draft ? <DraftPin x={ui.draft.x} y={ui.draft.y} color={ui.draft.color} d={pinDiameterPx(ui.draft.size, w)} /> : null}
      {ui.target ? <TargetMarker x={ui.target.x} y={ui.target.y} /> : null}
    </>
  );
});

/** Dashed frame with a white halo, so it stays visible over black notation (the page is always white). */
function SelectionFrame({ rect, pad }: { rect: Rect; pad: number }) {
  const box = { x: rect.x0 - pad, y: rect.y0 - pad, width: rect.x1 - rect.x0 + 2 * pad, height: rect.y1 - rect.y0 + 2 * pad, rx: 4 };
  return (
    <g>
      <rect {...box} fill="rgb(42 65 168 / 0.07)" stroke="#ffffff" strokeWidth={5} vectorEffect="non-scaling-stroke" />
      <rect {...box} fill="none" stroke={SELECTION_COLOR} strokeWidth={2.5} strokeDasharray="7 4" vectorEffect="non-scaling-stroke" />
    </g>
  );
}

/** Position of a handle at a bounds corner, pushed outwards by `offset` CSS px (sign −1 = top-left, 1 = bottom-right). */
function handleAt(x: number, y: number, H: number, offset: Pt, sign: 1 | -1) {
  return { left: `calc(${pct(x, UNITS_W)} + ${sign * offset.x}px)`, top: `calc(${pct(y, H)} + ${sign * offset.y}px)` };
}

/**
 * HTML hit areas over the selection (all ≥ 44 pt; geometry.ts selectionHitAt mirrors this layout and
 * stacking — keep them in sync): the box (drag = move; data-annot-handle "move" — for ink / a
 * highlight in Penni / Yfirstrikun only where it is on the mark itself, useEditInput), a move grip
 * outside the top-left frame corner ("grip") and the resize handle with corner arrows outside the
 * bottom-right one ("scale"), later in the DOM so they win where they overlap the box. `offset`
 * (handleOffsetPx) keeps the handles' targets off small objects. With `grab`, touch-action:none
 * keeps a finger drag on them from scrolling even when fingers navigate; without it (a Pencil-only
 * device, except Velja) a finger scrolls over the selection.
 */
function SelectionHandles({
  rect,
  offset,
  H,
  scalable,
  ghostPin,
  grab,
}: {
  rect: Rect;
  offset: Pt;
  H: number;
  scalable: boolean;
  ghostPin: { color: string; d: number } | null;
  grab: boolean;
}) {
  const t = useT();
  const touchAction = grab ? 'none' : 'auto';
  return (
    <>
      <div
        data-annot-handle="move"
        aria-hidden
        className="absolute z-30 -translate-x-1/2 -translate-y-1/2"
        style={{
          left: pct((rect.x0 + rect.x1) / 2, UNITS_W),
          top: pct((rect.y0 + rect.y1) / 2, H),
          width: `max(${HANDLE_TARGET_PX}px, ${pct(rect.x1 - rect.x0, UNITS_W)})`,
          height: `max(${HANDLE_TARGET_PX}px, ${pct(rect.y1 - rect.y0, H)})`,
          touchAction,
          cursor: 'move',
          pointerEvents: 'auto',
        }}
      >
        {ghostPin ? (
          <PinMark d={ghostPin.d} color={ghostPin.color} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-80" />
        ) : null}
      </div>
      {scalable ? (
        <>
          <div
            data-annot-handle="grip"
            role="presentation"
            title={t('annotate.sel_move')}
            className="absolute z-30 flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
            style={{ ...handleAt(rect.x0, rect.y0, H, offset, -1), touchAction, cursor: 'move', pointerEvents: 'auto' }}
          >
            <span
              className="flex size-7 items-center justify-center rounded-full border-2 bg-white shadow-md"
              style={{ borderColor: SELECTION_COLOR, color: SELECTION_COLOR }}
            >
              <Move className="size-4" strokeWidth={2.5} aria-hidden />
            </span>
          </div>
          <div
            data-annot-handle="scale"
            role="presentation"
            title={t('annotate.sel_resize')}
            className="absolute z-30 flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
            style={{ ...handleAt(rect.x1, rect.y1, H, offset, 1), touchAction, cursor: 'nwse-resize', pointerEvents: 'auto' }}
          >
            <span
              className="flex size-8 items-center justify-center rounded-full border-2 border-white text-white shadow-md"
              style={{ background: SELECTION_COLOR }}
            >
              <MoveDiagonal2 className="size-5" strokeWidth={2.5} aria-hidden />
            </span>
          </div>
        </>
      ) : null}
    </>
  );
}

/** Source of a connector being created: its pin at the Tengill size, with a ping around it. */
function DraftPin({ x, y, color, d }: { x: number; y: number; color: string; d: number }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute z-20 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center"
      style={{ left: `${x * 100}%`, top: `${y * 100}%`, width: d * 1.25, height: d * 1.25 }}
    >
      <span className="absolute inset-0 animate-ping rounded-full opacity-40" style={{ background: color }} />
      <PinMark d={d} color={color} className="relative" />
    </span>
  );
}

function TargetMarker({ x, y }: { x: number; y: number }) {
  const t = useT();
  return (
    <span className="pointer-events-none absolute z-20 size-0" style={{ left: `${x * 100}%`, top: `${y * 100}%` }}>
      <span
        className="absolute flex size-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[3px] bg-white/70"
        style={{ borderColor: SELECTION_COLOR, color: SELECTION_COLOR }}
      >
        <Crosshair className="size-7" aria-hidden />
      </span>
      <span
        className="absolute top-7 -translate-x-1/2 rounded-md bg-white/95 px-1.5 py-0.5 text-[13px] font-bold whitespace-nowrap shadow"
        style={{ color: SELECTION_COLOR }}
      >
        {t('annotate.conn_target_marker')}
      </span>
    </span>
  );
}
